import { useEffect, useId, useRef, useState } from 'react'

import { t } from 'lib/i18n'
import { activeLocale } from 'lib/region'

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
const AMMAN = { lat: 31.9539, lng: 35.9106 }

export interface MapPoint {
  latitude: number
  longitude: number
}

let mapsPromise: Promise<void> | null = null

const loadMaps = (language: string): Promise<void> => {
  if (window.google?.maps?.places) return Promise.resolve()
  if (!API_KEY) return Promise.reject(new Error('missing-key'))
  if (mapsPromise) return mapsPromise
  mapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}&libraries=places&language=${language}&region=JO&v=weekly`
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      mapsPromise = null
      reject(new Error('load-failed'))
    }
    document.head.appendChild(script)
  })
  return mapsPromise
}

const roundCoord = (value: number) => Math.round(value * 1e6) / 1e6

export const ProjectLocationMap = ({
  latitude,
  longitude,
  onChange,
}: {
  latitude: number | null
  longitude: number | null
  onChange: (point: MapPoint | null, address?: string) => void
}) => {
  const searchId = useId()
  const mapNode = useRef<HTMLDivElement>(null)
  const searchNode = useRef<HTMLInputElement>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(API_KEY ? 'loading' : 'error')

  useEffect(() => {
    const node = mapNode.current
    const search = searchNode.current
    if (!node || !search || !API_KEY) return
    let cancelled = false
    let map: google.maps.Map | null = null
    let marker: google.maps.Marker | null = null
    let autocomplete: google.maps.places.Autocomplete | null = null

    const language = activeLocale() === 'en' ? 'en' : 'ar'
    loadMaps(language)
      .then(() => {
        if (cancelled || !mapNode.current || !searchNode.current || !window.google?.maps?.places) return
        const hasPoint = latitude != null && longitude != null
        const center = hasPoint ? { lat: latitude, lng: longitude } : AMMAN
        map = new window.google.maps.Map(mapNode.current, {
          center,
          clickableIcons: false,
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          zoom: hasPoint ? 15 : 11,
        })
        marker = new window.google.maps.Marker({
          draggable: true,
          map,
          position: hasPoint ? center : undefined,
          visible: hasPoint,
        })
        markerRef.current = marker

        const apply = (position: google.maps.LatLng, address?: string) => {
          marker?.setPosition(position)
          marker?.setVisible(true)
          map?.panTo(position)
          onChangeRef.current({ latitude: roundCoord(position.lat()), longitude: roundCoord(position.lng()) }, address)
        }

        map.addListener('click', (event) => {
          if (!event.latLng) return
          apply(event.latLng)
        })
        marker.addListener('dragend', () => {
          const position = marker?.getPosition()
          if (!position) return
          apply(position)
        })

        autocomplete = new window.google.maps.places.Autocomplete(searchNode.current, {
          fields: ['geometry', 'formatted_address', 'name'],
        })
        autocomplete.bindTo('bounds', map)
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete?.getPlace()
          const location = place?.geometry?.location
          if (!location || !map) return
          if (place.geometry?.viewport) map.fitBounds(place.geometry.viewport)
          else map.setZoom(16)
          apply(location, place.formatted_address || place.name)
        })
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
      if (map) window.google?.maps.event.clearInstanceListeners(map)
      if (marker) window.google?.maps.event.clearInstanceListeners(marker)
      if (autocomplete) window.google?.maps.event.clearInstanceListeners(autocomplete)
      marker?.setMap(null)
      if (markerRef.current === marker) markerRef.current = null
      document.querySelectorAll('.pac-container').forEach((container) => container.remove())
    }
    // The map is created once. The parent remounts this form when the project changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selected = latitude != null && longitude != null

  const clear = () => {
    markerRef.current?.setVisible(false)
    if (searchNode.current) searchNode.current.value = ''
    onChange(null)
  }

  return (
    <div className="location-map span-2">
      <div className="location-map__head">
        <label className="field__label" htmlFor={searchId}>
          {t('موقع المشروع على الخريطة', 'Project location on the map')}
        </label>
        {selected ? (
          <button type="button" className="link-button" onClick={clear}>
            {t('إزالة الموقع', 'Clear location')}
          </button>
        ) : null}
      </div>
      <input
        id={searchId}
        ref={searchNode}
        className="input"
        type="search"
        placeholder={t('ابحث عن العنوان أو اسم الموقع', 'Search for an address or place')}
        autoComplete="off"
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault()
        }}
      />
      <div className="location-map__frame">
        <div className="location-map__canvas" ref={mapNode} />
        {status === 'loading' ? <span className="location-map__status">{t('جارٍ تحميل الخريطة', 'Loading map')}</span> : null}
      </div>
      {status === 'error' ? (
        <p className="field__error">
          {API_KEY
            ? t('تعذر تحميل خرائط جوجل. تحقق من الاتصال ثم أعد فتح الصفحة.', 'Google Maps could not be loaded. Check the connection and reopen the page.')
            : t('مفتاح خرائط جوجل غير مضبوط.', 'The Google Maps key is not configured.')}
        </p>
      ) : null}
      <p className="field__hint">
        {selected
          ? t(
              `النقطة المحددة: ${latitude}, ${longitude}. انقر على الخريطة أو اسحب الدبوس للتعديل. تُستخدم لتوقعات الطقس.`,
              `Selected point: ${latitude}, ${longitude}. Click the map or drag the pin to adjust it. Used for the weather forecast.`
            )
          : t(
              'ابحث أو انقر على الخريطة لتحديد موقع المشروع. تُستخدم النقطة لتوقعات الطقس في نظرة عامة على الموقع.',
              'Search or click the map to set the project location. The point is used for the weather forecast on the site Overview.'
            )}
      </p>
    </div>
  )
}
